import { useState, useEffect } from "react"
import Navbar from "../../components/Navbar"
import PageTitle from "../../components/PageTitle"
import { allFirstDiv } from "../../components/DesignStandardize"
import { toast } from "react-toastify"

const HolidayCRUD = () => {
  const [holidays, setHolidays] = useState([])
  const [newHoliday, setNewHoliday] = useState({ 
    DateFrom: "", 
    DateTo: "", 
    Description: "",
    isRange: false 
  })
  const [editingHoliday, setEditingHoliday] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const apiUrl = process.env.REACT_APP_API_BASE_URL

  useEffect(() => {
    fetchHolidays()
  }, [])

  const fetchHolidays = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${apiUrl}/holiday`)
      if (!response.ok) throw new Error("Failed to fetch holidays")
      const data = await response.json()
      // Sort holidays by date in descending order (latest first)
      const sortedData = data.sort((a, b) => new Date(b.Date) - new Date(a.Date))
      setHolidays(sortedData)
    } catch (err) {
      setError("Failed to load holidays")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    const inputValue = type === "checkbox" ? checked : value
    
    if (editingHoliday) {
      setEditingHoliday({ ...editingHoliday, [name]: inputValue })
    } else {
      setNewHoliday({ ...newHoliday, [name]: inputValue })
    }
  }

  // Generate array of dates between two dates
  const getDateRange = (startDate, endDate) => {
    const dates = []
    const currentDate = new Date(startDate)
    const lastDate = new Date(endDate)

    while (currentDate <= lastDate) {
      dates.push(new Date(currentDate).toISOString().split('T')[0])
      currentDate.setDate(currentDate.getDate() + 1)
    }
    return dates
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      if (editingHoliday) {
        await updateHoliday(editingHoliday)
        toast.success("Holiday updated successfully!")
      } else {
        // Check if it's a date range
        if (newHoliday.isRange && newHoliday.DateFrom && newHoliday.DateTo) {
          const dates = getDateRange(newHoliday.DateFrom, newHoliday.DateTo)
          
          // Create individual holidays for each date
          let successCount = 0
          for (const date of dates) {
            try {
              await createHoliday({ Date: date, Description: newHoliday.Description })
              successCount++
            } catch (err) {
              console.error(`Failed to create holiday for ${date}:`, err)
            }
          }
          toast.success(`${successCount} holiday(s) added successfully!`)
        } else {
          // Single date
          await createHoliday({ 
            Date: newHoliday.DateFrom, 
            Description: newHoliday.Description 
          })
          toast.success("Holiday added successfully!")
        }
      }
      
      fetchHolidays()
      setNewHoliday({ DateFrom: "", DateTo: "", Description: "", isRange: false })
      setEditingHoliday(null)
    } catch (err) {
      setError("Failed to save holiday")
      toast.error("Failed to save holiday")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const createHoliday = async (holiday) => {
    const response = await fetch(`${apiUrl}/holiday`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(holiday),
    })
    if (!response.ok) throw new Error("Failed to create holiday")
  }

  const updateHoliday = async (holiday) => {
    const response = await fetch(`${apiUrl}/holiday/${holiday.HolidayID}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(holiday),
    })
    if (!response.ok) throw new Error("Failed to update holiday")
  }

  const deleteHoliday = async (id) => {
    if (!window.confirm("Are you sure you want to delete this holiday?")) {
      return
    }
    
    setLoading(true)
    try {
      const response = await fetch(`${apiUrl}/holiday/${id}`, { method: "DELETE" })
      if (!response.ok) throw new Error("Failed to delete holiday")
      toast.success("Holiday deleted successfully!")
      fetchHolidays()
    } catch (err) {
      setError("Failed to delete holiday")
      toast.error("Failed to delete holiday")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Navbar />
      <PageTitle title="Holiday Management" />
      <div className={`${allFirstDiv} min-h-screen bg-[#F5F5F5] py-12 px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-7xl mx-auto space-y-8">
          <h1 className="text-4xl font-extrabold text-[#2D2D2D] text-center mt-5">Holiday Management</h1>

          {/* Form Card */}
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-sm">
            <div className="p-6 sm:p-8">
              <h2 className="text-xl font-semibold text-[#2D2D2D] mb-6">
                {editingHoliday ? "Edit Holiday" : "Add New Holiday"}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                {!editingHoliday && (
                  <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="isRange"
                      name="isRange"
                      checked={newHoliday.isRange}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="isRange" className="text-sm font-medium text-gray-700">
                      Add multiple holidays (date range)
                    </label>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="DateFrom" className="block text-sm font-medium text-gray-700 mb-1">
                      {editingHoliday ? "Date" : (newHoliday.isRange ? "From Date" : "Date")}
                    </label>
                    <input
                      type="date"
                      id="DateFrom"
                      name={editingHoliday ? "Date" : "DateFrom"}
                      value={editingHoliday ? editingHoliday.Date : newHoliday.DateFrom}
                      onChange={handleInputChange}
                      required
                      className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    />
                  </div>

                  {!editingHoliday && newHoliday.isRange && (
                    <div>
                      <label htmlFor="DateTo" className="block text-sm font-medium text-gray-700 mb-1">
                        To Date
                      </label>
                      <input
                        type="date"
                        id="DateTo"
                        name="DateTo"
                        value={newHoliday.DateTo}
                        onChange={handleInputChange}
                        min={newHoliday.DateFrom}
                        required={newHoliday.isRange}
                        className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="Description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="Description"
                    name="Description"
                    value={editingHoliday ? editingHoliday.Description : newHoliday.Description}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    placeholder="e.g., Independence Day, Eid-ul-Fitr, etc."
                    className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition resize-none"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                  >
                    {loading ? "Saving..." : editingHoliday ? "Update Holiday" : "Add Holiday"}
                  </button>
                  
                  {editingHoliday && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingHoliday(null)
                        setNewHoliday({ DateFrom: "", DateTo: "", Description: "", isRange: false })
                      }}
                      className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition duration-200"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* Holidays List */}
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-sm">
            <div className="p-6 sm:p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-[#2D2D2D]">Holidays List</h2>
                <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {holidays.length} {holidays.length === 1 ? 'Holiday' : 'Holidays'}
                </span>
              </div>
              
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                  {error}
                </div>
              )}
              
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : holidays.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="mt-2 text-lg">No holidays added yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {holidays.map((holiday, index) => (
                        <tr 
                          key={holiday.HolidayID}
                          className="hover:bg-blue-50 transition duration-150"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <svg className="h-5 w-5 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className="text-sm font-medium text-gray-900">
                                {new Date(holiday.Date).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700">{holiday.Description}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <button
                              onClick={() => setEditingHoliday(holiday)}
                              className="inline-flex items-center px-3 py-1.5 bg-blue-100 text-blue-700 text-sm font-medium rounded-md hover:bg-blue-200 transition mr-2"
                            >
                              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit
                            </button>
                            <button
                              onClick={() => deleteHoliday(holiday.HolidayID)}
                              className="inline-flex items-center px-3 py-1.5 bg-red-100 text-red-700 text-sm font-medium rounded-md hover:bg-red-200 transition"
                            >
                              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default HolidayCRUD

